import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import {
  Search, Plus, Filter, ChevronLeft, ChevronRight,
  School, MoreVertical, CheckCircle, XCircle, Trash2, Eye,
} from "lucide-react";
import SuperAdminLayout from "@/components/ui/superadmin/SuperAdminLayout";
import { superAdminApi, type AdminSchool } from "@/hooks/api/superAdminApi";

const STATUS_COLORS: Record<string, string> = {
  active:    "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  inactive:  "bg-slate-700/50 text-slate-400 border-slate-600/30",
  pending:   "bg-amber-500/10 text-amber-400 border-amber-500/20",
  suspended: "bg-red-500/10 text-red-400 border-red-500/20",
};

const TIER_COLORS: Record<string, string> = {
  basic:      "bg-slate-700 text-slate-300",
  standard:   "bg-blue-500/20 text-blue-400",
  premium:    "bg-violet-500/20 text-violet-400",
  enterprise: "bg-amber-500/20 text-amber-400",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_COLORS[status] ?? STATUS_COLORS.inactive}`}>
      {status}
    </span>
  );
}

function TierBadge({ tier }: { tier: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${TIER_COLORS[tier] ?? TIER_COLORS.basic}`}>
      {tier}
    </span>
  );
}

function ActionMenu({ school, onStatusChange, onDelete }: {
  school: AdminSchool;
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="p-1.5 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-20 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-1 w-44">
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700 transition-colors"
              onClick={() => { setOpen(false); navigate(`/admin/schools/${school._id}`); }}
            >
              <Eye className="w-3.5 h-3.5" /> View details
            </button>
            {school.status !== "active" ? (
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-emerald-400 hover:bg-slate-700 transition-colors"
                onClick={() => { setOpen(false); onStatusChange(school._id, "active"); }}
              >
                <CheckCircle className="w-3.5 h-3.5" /> Activate
              </button>
            ) : (
              <button
                className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-amber-400 hover:bg-slate-700 transition-colors"
                onClick={() => { setOpen(false); onStatusChange(school._id, "suspended"); }}
              >
                <XCircle className="w-3.5 h-3.5" /> Suspend
              </button>
            )}
            <div className="my-1 border-t border-slate-700" />
            <button
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-red-400 hover:bg-slate-700 transition-colors"
              onClick={() => { setOpen(false); onDelete(school._id); }}
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete school
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function SchoolsManagement() {
  const navigate = useNavigate();
  const [schools, setSchools]   = useState<AdminSchool[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tierFilter, setTierFilter]     = useState("");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const res = await superAdminApi.listSchools({
        page: p, limit: 15,
        search: search || undefined,
        status: statusFilter || undefined,
        tier: tierFilter || undefined,
      });
      setSchools(res.schools);
      setTotal(res.total);
      setPages(res.pages);
    } catch {
      // error silently shown by empty state
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, tierFilter]);

  useEffect(() => { load(1); setPage(1); }, [search, statusFilter, tierFilter]);
  useEffect(() => { load(page); }, [page]);

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await superAdminApi.updateSchoolStatus(id, status);
      setSchools((prev) => prev.map((s) => s._id === id ? { ...s, status: status as AdminSchool["status"] } : s));
    } catch {}
  };

  const handleDelete = async (id: string) => {
    try {
      await superAdminApi.deleteSchool(id);
      setSchools((prev) => prev.filter((s) => s._id !== id));
      setTotal((t) => t - 1);
    } catch {}
    setConfirmDelete(null);
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Schools</h1>
            <p className="text-slate-400 text-sm mt-0.5">{total.toLocaleString()} schools on the platform</p>
          </div>
          <button
            onClick={() => navigate("/admin/schools/new")}
            className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold rounded-xl transition-colors shrink-0"
          >
            <Plus className="w-4 h-4" /> Add School
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search by name, code or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="px-3 py-2.5 bg-slate-900 border border-slate-700 rounded-xl text-sm text-slate-300 focus:outline-none focus:border-teal-500 cursor-pointer"
            >
              <option value="">All tiers</option>
              <option value="basic">Basic</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">School</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Location</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Tier</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Students</th>
                  <th className="text-left px-4 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden xl:table-cell">Admin</th>
                  <th className="px-4 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i}>
                      {[1,2,3,4,5,6,7].map((j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-slate-800 rounded animate-pulse" style={{ width: `${60 + (j * 13) % 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : schools.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center text-slate-500">
                      <School className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>No schools found</p>
                    </td>
                  </tr>
                ) : (
                  schools.map((s) => (
                    <tr
                      key={s._id}
                      onClick={() => navigate(`/admin/schools/${s._id}`)}
                      className="hover:bg-slate-800/50 cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4">
                        <p className="font-semibold text-white">{s.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{s.code} · {s.email}</p>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell text-slate-400">
                        {s.address?.city}, {s.address?.country}
                      </td>
                      <td className="px-4 py-4">
                        <StatusBadge status={s.status} />
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <TierBadge tier={s.tier} />
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell text-slate-400">
                        {s.statistics?.totalStudents ?? 0}
                      </td>
                      <td className="px-4 py-4 hidden xl:table-cell text-slate-400 text-xs">
                        {typeof s.adminId === "object" && s.adminId ? s.adminId.name : "—"}
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <ActionMenu school={s} onStatusChange={handleStatusChange} onDelete={(id) => setConfirmDelete(id)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between px-5 py-3.5 border-t border-slate-800">
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page === pages}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Delete school?</h3>
                <p className="text-sm text-slate-400">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-500 text-white text-sm font-semibold transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </SuperAdminLayout>
  );
}
